// src/lib/mutationQueue.js
//
// Stage 3 — Offline mutation queue.
//
// User-initiated state changes (archive, trash, star, mark read/unread)
// used to fire the server call synchronously. If the network was flaky or
// Supabase was slow, the UI would hang or the action would fail with a
// toast. This queue decouples the intent from the execution:
//
//   1. User clicks Archive → enqueue({ action: 'archive_email', args }) and
//      optimistically update the UI (caller's responsibility).
//   2. The queue fires the server call in the background. On success, drop
//      the queued row; on retriable error, schedule exponential backoff; on
//      permanent error (auth lost, 400 validation), mark as 'failed' for
//      user review.
//   3. Online / focus events drain the queue automatically. Manual
//      drainQueue() works too.
//
// Persisted via the same Dexie DB as the entityCache. Survives reloads so
// actions taken on a plane flush when wifi is back.
//
// IMPORTANT: send/compose is NOT wired through this yet — the payload is
// bigger (attachments, HTML body, threading) and the semantics are more
// fragile. Covered by a separate follow-up commit.

import Dexie from 'dexie'

const DB_NAME = 'ChatroneMutationQueue'
const MAX_ATTEMPTS = 5

// Status lifecycle: pending → in_flight → (done|failed).
// pending entries wait for a drain trigger. in_flight entries are currently
// mid-request (or left over from a crashed tab — reaped on next drain).

const db = new Dexie(DB_NAME)
db.version(1).stores({
  mutations: '++id, status, action, createdAt',
})

let _postAction = null  // injected — the actual network call
let _onChange = null    // injected — notifies UI of queue changes
let _onFailed = null    // injected — told when a row stops retrying (action, args, err, permanent)

let _draining = false
let _drainTimer = null

export function initMutationQueue({ postAction, onChange, onFailed } = {}) {
  _postAction = postAction || null
  _onChange   = onChange || null
  _onFailed   = onFailed || null

  // Reap any in-flight rows from a prior crashed tab — they never completed
  // so they're effectively still pending.
  db.mutations.where('status').equals('in_flight').modify({ status: 'pending' })
    .catch(e => console.warn('[mutationQueue] reap failed:', e?.message))

  // Trigger drain on reconnect and focus — both are classic "we're online
  // again" signals. Debounced so rapid focus flipping doesn't thrash.
  if (typeof window !== 'undefined') {
    window.addEventListener('online', scheduleDrain)
    window.addEventListener('focus', scheduleDrain)
    // Initial drain once the page settles — wait 1s so boot isn't competing
    // with initial data fetches.
    setTimeout(scheduleDrain, 1000)
  }
}

function scheduleDrain(delayMs = 100) {
  if (_drainTimer) return
  _drainTimer = setTimeout(() => {
    _drainTimer = null
    drainQueue().catch(e => console.warn('[mutationQueue] drain failed:', e?.message))
  }, delayMs)
}

// Enqueue a mutation. Returns the local id — callers can track it if they
// want (not required).
export async function enqueue(action, args = {}) {
  const row = {
    action,
    args,
    status: 'pending',
    attempts: 0,
    lastError: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }
  const id = await db.mutations.add(row)
  _onChange && _onChange()
  scheduleDrain()
  return id
}

// Drain the queue. Each pending mutation fires once; retriable failures
// schedule a later re-drain with backoff. Guards against concurrent drains.
export async function drainQueue() {
  if (_draining) return
  if (!_postAction) {
    console.warn('[mutationQueue] no postAction registered — cannot drain')
    return
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  _draining = true
  try {
    const pending = await db.mutations.where('status').equals('pending').sortBy('createdAt')
    for (const row of pending) {
      await processOne(row)
    }
  } finally {
    _draining = false
    _onChange && _onChange()
  }
}

async function processOne(row) {
  // Claim the row so concurrent drains don't double-fire.
  await db.mutations.update(row.id, { status: 'in_flight', updatedAt: Date.now() })
  try {
    await _postAction(row.action, row.args)
    await db.mutations.delete(row.id)
  } catch (err) {
    const attempts = (row.attempts || 0) + 1
    const isPermanent = isPermanentError(err)
    const finalStatus = (isPermanent || attempts >= MAX_ATTEMPTS) ? 'failed' : 'pending'
    await db.mutations.update(row.id, {
      status: finalStatus,
      attempts,
      lastError: err?.message || String(err),
      updatedAt: Date.now(),
    })
    if (finalStatus === 'failed' && _onFailed) {
      try { _onFailed(row.action, row.args, err, isPermanent) } catch (e) { console.warn('[mutationQueue] onFailed hook failed:', e?.message) }
    }
    if (finalStatus === 'pending') {
      // Exponential backoff: 2s, 4s, 8s, 16s, 32s (capped)
      const delay = Math.min(2000 * 2 ** (attempts - 1), 32000)
      setTimeout(scheduleDrain, delay)
    }
  }
}

// Errors we should not retry. 400-series except 408/429 are usually permanent;
// also anything with "Invalid" / "not found" / "forbidden" in the message.
function isPermanentError(err) {
  const msg = (err?.message || '').toLowerCase()
  if (msg.includes('invalid') || msg.includes('forbidden') || msg.includes('not found')) return true
  // HTTP-code patterns — postAction typically throws "HTTP 400" style messages.
  const m = msg.match(/\bhttp\s*(\d{3})\b/) || msg.match(/\b(\d{3})\b/)
  if (!m) return false
  const code = parseInt(m[1])
  if (code === 408 || code === 429) return false   // retry
  if (code >= 400 && code < 500) return true        // don't retry client errors
  return false
}

// UI helpers — expose queue state so components can show a badge / banner.
export async function getPendingCount() {
  try {
    return await db.mutations.where('status').anyOf('pending', 'in_flight').count()
  } catch { return 0 }
}

export async function getFailedCount() {
  try {
    return await db.mutations.where('status').equals('failed').count()
  } catch { return 0 }
}

// Retry a failed mutation — moves it back to pending and triggers drain.
export async function retryFailed(id) {
  await db.mutations.update(id, { status: 'pending', attempts: 0, lastError: null })
  scheduleDrain()
  _onChange && _onChange()
}

export async function retryAllFailed() {
  await db.mutations.where('status').equals('failed').modify({ status: 'pending', attempts: 0, lastError: null })
  scheduleDrain()
  _onChange && _onChange()
}

export async function discardFailed(id) {
  await db.mutations.delete(id)
  _onChange && _onChange()
}

// Diagnostic — for dev console.
export async function queueStats() {
  try {
    const byStatus = {}
    await db.mutations.each(r => { byStatus[r.status] = (byStatus[r.status] || 0) + 1 })
    return byStatus
  } catch (err) {
    return { error: err?.message }
  }
}

if (typeof window !== 'undefined') {
  window.__queueStats = queueStats
  window.__drainQueue = drainQueue
}
